import React, { useState, useEffect, useContext } from 'react';
import {
    Container, Paper, Typography, Button, Box, TextField, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, CircularProgress, IconButton, Divider, useTheme, useMediaQuery,
    Chip, Tooltip
} from '@mui/material';
import {
    Edit as EditIcon, Delete as DeleteIcon,
    BarChart as MetricsIcon, TrendingUp, People, TouchApp,
    AutorenewRounded as AutoIcon, CalendarMonth as CalendarIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import metricsService from '../../services/metricsService';

const AdminMetricsManager = () => {
    const { currentUser } = useContext(AuthContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [metrics, setMetrics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Dialog states
    const [editDialog, setEditDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState(null);

    const [formData, setFormData] = useState({});

    // Start date state
    const [startDate, setStartDate] = useState('');
    const [startDateLoading, setStartDateLoading] = useState(false);

    useEffect(() => {
        loadMetrics();
        loadStartDate();
    }, []);

    const loadMetrics = async () => {
        try {
            setLoading(true);
            // GET /api/metrics/ also auto-creates the current month
            const data = await metricsService.getMetrics();
            setMetrics(data);
        } catch (err) {
            setError('Failed to load metrics. ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadStartDate = async () => {
        try {
            const data = await metricsService.getStartDate();
            setStartDate(data.start_date || '');
        } catch (err) {
            console.error('Failed to load start date:', err);
        }
    };

    const saveStartDate = async () => {
        try {
            setStartDateLoading(true);
            setError('');
            const token = localStorage.getItem('accessToken');
            await metricsService.updateStartDate(startDate, token);
            setSuccess('Metrics start date updated successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to save start date: ' + err.message);
        } finally {
            setStartDateLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseFloat(value) || 0 : value
        }));
    };

    const INT_FIELDS = ['mau', 'dau', 'core_features_used', 'clicks', 'impressions', 'uk_users', 'nigeria_users'];
    const FLOAT_FIELDS = ['retention_30_day', 'retention_90_day', 'retention_120_day', 'avg_sessions_per_user', 'avg_session_duration', 'growth_rate'];

    const handleSave = async () => {
        try {
            setError('');
            const token = localStorage.getItem('accessToken');
            // Send only the editable fields (exclude month, id, timestamps, manual_adjustments)
            const { id, month, created_at, updated_at, manual_adjustments, ...editableFields } = formData;
            // Ensure correct types before sending to API
            const cleanedFields = {};
            for (const [key, value] of Object.entries(editableFields)) {
                if (INT_FIELDS.includes(key)) {
                    cleanedFields[key] = Math.round(Number(value) || 0);
                } else if (FLOAT_FIELDS.includes(key)) {
                    cleanedFields[key] = parseFloat(Number(value) || 0);
                } else {
                    cleanedFields[key] = value;
                }
            }
            await metricsService.updateMetric(selectedMetric.id, cleanedFields, token);
            setSuccess('Metrics updated successfully');
            setEditDialog(false);
            setSelectedMetric(null);
            loadMetrics();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async () => {
        try {
            setError('');
            const token = localStorage.getItem('accessToken');
            await metricsService.deleteMetric(selectedMetric.id, token);
            setSuccess('Metric deleted successfully');
            setDeleteDialog(false);
            setSelectedMetric(null);
            loadMetrics();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        }
    };

    const openEditDialog = (metric) => {
        setSelectedMetric(metric);
        setFormData({ ...metric });
        setEditDialog(true);
    };

    const openDeleteDialog = (metric) => {
        setSelectedMetric(metric);
        setDeleteDialog(true);
    };

    const formatMonth = (monthStr) => {
        const date = new Date(monthStr + 'T00:00:00');
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    const isCurrentMonth = (monthStr) => {
        const now = new Date();
        const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        return monthStr === current;
    };

    // Admin guard
    if (!currentUser?.is_admin) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">Access denied. Admin privileges required.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress size={60} />
            </Container>
        );
    }

    const renderFormFields = () => (
        <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Month display (read-only) */}
            <Grid size={12}>
                <TextField
                    label="Month"
                    value={selectedMetric ? formatMonth(selectedMetric.month) : ''}
                    fullWidth
                    disabled
                    InputLabelProps={{ shrink: true }}
                    helperText="All values are auto-computed from user activity. You can still override any value below."
                />
            </Grid>

            {/* Active Users */}
            <Grid size={12}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                    Active Users
                </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
                <TextField label="Monthly Active Users (MAU)" type="number" name="mau" value={formData.mau ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0 }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
                <TextField label="Daily Active Users (DAU)" type="number" name="dau" value={formData.dau ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0 }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
                <TextField label="Growth Rate (%)" type="number" name="growth_rate" value={formData.growth_rate ?? 0} onChange={handleInputChange} fullWidth inputProps={{ step: 0.1 }} />
            </Grid>

            {/* Retention Rates */}
            <Grid size={12}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                    User Retention Rates (%)
                </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
                <TextField label="30-Day Retention (%)" type="number" name="retention_30_day" value={formData.retention_30_day ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0, max: 100, step: 0.1 }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
                <TextField label="90-Day Retention (%)" type="number" name="retention_90_day" value={formData.retention_90_day ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0, max: 100, step: 0.1 }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
                <TextField label="120-Day Retention (%)" type="number" name="retention_120_day" value={formData.retention_120_day ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0, max: 100, step: 0.1 }} />
            </Grid>

            {/* Engagement */}
            <Grid size={12}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                    User Engagement Depth
                </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
                <TextField label="Avg Sessions/User" type="number" name="avg_sessions_per_user" value={formData.avg_sessions_per_user ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0, step: 0.1 }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
                <TextField label="Avg Session Duration (min)" type="number" name="avg_session_duration" value={formData.avg_session_duration ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0, step: 0.1 }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
                <TextField label="Core Features Used" type="number" name="core_features_used" value={formData.core_features_used ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0 }} />
            </Grid>

            {/* Clicks & Impressions */}
            <Grid size={12}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                    Clicks & Impressions
                    <Chip label="Auto-tracked" size="small" icon={<AutoIcon />} color="info" variant="outlined" sx={{ ml: 1 }} />
                </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
                <TextField label="Total Clicks" type="number" name="clicks" value={formData.clicks ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0 }}
                    helperText="Auto-incremented on user clicks. You can also adjust manually." />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <TextField label="Total Impressions" type="number" name="impressions" value={formData.impressions ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0 }}
                    helperText="Auto-incremented on page views. You can also adjust manually." />
            </Grid>

            {/* Geographic */}
            <Grid size={12}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                    Geographic Distribution
                </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
                <TextField label="UK Users" type="number" name="uk_users" value={formData.uk_users ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0 }} />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <TextField label="Nigeria Users" type="number" name="nigeria_users" value={formData.nigeria_users ?? 0} onChange={handleInputChange} fullWidth inputProps={{ min: 0 }} />
            </Grid>
        </Grid>
    );

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 2, md: 3 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: 2 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MetricsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 600 }}>
                            Performance Metrics
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        All metrics auto-compute from user activity. Country is detected from IP. You can override any value.
                    </Typography>
                </Box>
            </Box>

            {/* Alerts */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Metrics Start Date Setting */}
            <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 3, borderLeft: '4px solid #9c27b0' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                        <CalendarIcon sx={{ color: '#9c27b0' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Tracking Start Date
                        </Typography>
                    </Box>
                    <TextField
                        type="date"
                        size="small"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 180 }}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={startDateLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={saveStartDate}
                        disabled={startDateLoading || !startDate}
                        sx={{ minWidth: 100 }}
                    >
                        Save
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 1 } }}>
                        Specifies when metrics tracking officially began for this application.
                    </Typography>
                </Box>
            </Paper>

            {/* Summary Cards */}
            {metrics.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Card elevation={2} sx={{ borderLeft: '4px solid #1976d2' }}>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                <People sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }} />
                                <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                                    {metrics[0]?.mau?.toLocaleString() || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Latest MAU</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Card elevation={2} sx={{ borderLeft: '4px solid #dc004e' }}>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                <People sx={{ fontSize: 28, color: 'secondary.main', mb: 0.5 }} />
                                <Typography variant="h5" color="secondary" sx={{ fontWeight: 700 }}>
                                    {metrics[0]?.dau?.toLocaleString() || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Latest DAU</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Card elevation={2} sx={{ borderLeft: '4px solid #4caf50' }}>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                <TrendingUp sx={{ fontSize: 28, color: 'success.main', mb: 0.5 }} />
                                <Typography variant="h5" color="success.main" sx={{ fontWeight: 700 }}>
                                    {metrics[0]?.growth_rate || 0}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Latest Growth</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Card elevation={2} sx={{ borderLeft: '4px solid #ff9800' }}>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                <TouchApp sx={{ fontSize: 28, color: 'warning.main', mb: 0.5 }} />
                                <Typography variant="h5" color="warning.main" sx={{ fontWeight: 700 }}>
                                    {metrics[0]?.clicks?.toLocaleString() || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Clicks (Current Month)</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Metrics Table */}
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Month</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">MAU</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }} align="right">DAU</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }} align="right">Growth</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }} align="right">Retention 30d</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', lg: 'table-cell' } }} align="right">Clicks</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600, display: { xs: 'none', lg: 'table-cell' } }} align="right">Impressions</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {metrics.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                        <MetricsIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
                                        <Typography variant="h6" color="text.secondary">No metrics recorded yet</Typography>
                                        <Typography variant="body2" color="text.secondary">Metrics will appear automatically as users interact with the application</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                metrics.map((metric) => (
                                    <TableRow key={metric.id} hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip label={formatMonth(metric.month)} color="primary" variant="outlined" size="small" />
                                                {isCurrentMonth(metric.month) && (
                                                    <Chip label="Current" size="small" color="success" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">{metric.mau.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{metric.dau.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            <Chip
                                                label={`${metric.growth_rate}%`}
                                                size="small"
                                                color={metric.growth_rate >= 0 ? 'success' : 'error'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{metric.retention_30_day}%</TableCell>
                                        <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{metric.clicks.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{metric.impressions.toLocaleString()}</TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Edit values">
                                                <IconButton size="small" color="primary" onClick={() => openEditDialog(metric)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete month">
                                                <IconButton size="small" color="error" onClick={() => openDeleteDialog(metric)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Edit Dialog */}
            <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon color="primary" />
                        Edit Metrics — {selectedMetric ? formatMonth(selectedMetric.month) : ''}
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {renderFormFields()}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditDialog(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the metrics for{' '}
                        <strong>{selectedMetric ? formatMonth(selectedMetric.month) : ''}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminMetricsManager;
